/**
 * Format an ISO date string as a short, human-readable date.
 *
 * @param {string} dateStr - ISO date string
 * @returns {string} E.g. "Sep 13, 2026", or "" if dateStr is falsy
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Snake_case fallback for any metric key not covered by a type's curated label map (e.g. an
 * unrecognized attribute the backend starts returning).
 *
 * @param {string} key - Metric key, e.g. "room_quality" or "AEO"
 * @returns {string} Title-cased label, or the key unchanged if it's all-uppercase
 */
export const humanize = (key) =>
  key === key.toUpperCase()
    ? key
    : key
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

/**
 * Resolve a metric's display label from a type's curated label map, falling back to a humanized
 * version of the key.
 *
 * @param {string} key - Metric key
 * @param {Record<string, string>} metricLabels - Curated label map for the report type
 * @returns {string} Display label
 */
export const labelForMetric = (key, metricLabels) => metricLabels[key] ?? humanize(key);

/**
 * Deterministic, instant insight for a specific metric line — the server only generates one LLM
 * insight per group (for the composite score), so switching which metric is charted recomputes a
 * lightweight sentence client-side rather than either lying (showing the composite's insight under
 * a different metric) or firing a fresh LLM call on every dropdown change.
 *
 * @param {{ date: string; metrics?: Record<string, number | null> }[]} series - Trend points
 * @param {string} metricKey - Which metric in `metrics` to summarize
 * @param {string} metricLabel - Display label for that metric
 * @returns {string | null} A one-sentence trend summary, or null if fewer than two points have the
 *   metric
 */
export const computeMetricInsight = (series, metricKey, metricLabel) => {
  const points = series.filter(
    (p) => p.metrics?.[metricKey] !== undefined && p.metrics[metricKey] !== null,
  );
  if (points.length < 2) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const delta = Math.round((last.metrics[metricKey] - first.metrics[metricKey]) * 100) / 100;
  const since = `since ${formatDate(first.date)}`;
  if (Math.abs(delta) < 0.005) return `${metricLabel} has held steady ${since}.`;
  const direction = delta > 0 ? "up" : "down";
  return `${metricLabel} is ${direction} ${Math.abs(delta)} ${since}.`;
};
